<script setup>
import { ref, computed, watch } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  isTeamAttendant,
  isTeamRotationEnabled,
  isTeamSupervisor,
} from '../helpers/memberRoles';

// Aceleres — Fase 1: gerenciar membros da equipe num Modal nativo, reusando o
// AgentSelector (tabela de agentes com checkboxes) + a API team_members.
// Aceleres — Fase 2: seção "Papel" (Atendente + Supervisor) por membro. Supervisor
// vê/responde pra intervir, mas só marca a conversa como lida quando responde
// (regra no backend: hook supervisor_reply_marks_read + patch de view
// assignee_only_read). PATCH imediato, só pra membros já persistidos.
const props = defineProps({
  team: { type: Object, default: null },
});
const emit = defineEmits(['close', 'saved']);

const store = useStore();
const agents = useMapGetter('agents/getAgents');

const selected = ref([]);
const searchQuery = ref('');
const saving = ref(false);
const roleBusy = ref({}); // userId -> bool (PATCH em andamento)
const pendingMemberSettings = ref({});

const modalText = {
  title: 'Membros',
  description:
    'Marque os usuários que fazem parte desta equipe. Um atendente também pode ser supervisor.',
  saveMembers: 'Salvar membros',
  selectAll: 'Selecionar todos',
  searchPlaceholder: 'Buscar por nome ou e-mail',
  noSearchResults: 'Nenhum usuário encontrado.',
  rolesTitle: 'Papel do membro',
  rolesDescription:
    'A participação no rodízio e o papel de supervisor são independentes. Um administrador só recebe conversas quando participa do rodízio.',
  attendantRole: 'Participa do rodízio',
  supervisorRole: 'Supervisor',
  nonAttendantHint: 'Fora do rodízio',
};

const loadedMembers = computed(
  () => store.getters['teamMembers/getTeamMembers'](props.team?.id) || []
);
const memberIds = computed(() => loadedMembers.value.map(m => m.id));
const modalTitle = computed(() =>
  props.team ? `${modalText.title} - ${props.team.name || ''}` : modalText.title
);
const dialogLabel = computed(() =>
  props.team ? `${modalText.title} - ${props.team.name || ''}` : modalText.title
);
const selectedCountLabel = computed(() =>
  selected.value.length === 1
    ? '1 membro selecionado'
    : `${selected.value.length} membros selecionados`
);

const close = () => emit('close');

const load = async () => {
  if (!props.team) return;
  searchQuery.value = '';
  pendingMemberSettings.value = {};
  store.dispatch('agents/get');
  try {
    await store.dispatch('teamMembers/get', { teamId: props.team.id });
  } catch (e) {
    // ignore
  }
  selected.value = [...memberIds.value];
};

watch(() => props.team, load, { immediate: true });

watch(
  () => !!props.team,
  (isOpen, _, onCleanup) => {
    const onEsc = event => {
      if (event.key === 'Escape') {
        close();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', onEsc);
      onCleanup(() => document.removeEventListener('keydown', onEsc));
      return;
    }

    document.removeEventListener('keydown', onEsc);
  }
);

const updateSelected = ids => {
  selected.value = [...ids];
};

const loadedMemberById = userId =>
  loadedMembers.value.find(member => member.id === userId);

const agentById = userId => agents.value.find(agent => agent.id === userId);

const defaultSettingsFor = userId => ({
  role: 'user',
  rotationEnabled: isTeamAttendant(agentById(userId)),
});

const ensurePendingSettings = userId => {
  if (loadedMemberById(userId) || pendingMemberSettings.value[userId]) return;

  pendingMemberSettings.value = {
    ...pendingMemberSettings.value,
    [userId]: defaultSettingsFor(userId),
  };
};

const removePendingSettings = userIds => {
  const ids = new Set(userIds);
  pendingMemberSettings.value = Object.fromEntries(
    Object.entries(pendingMemberSettings.value).filter(
      ([userId]) => !ids.has(Number(userId))
    )
  );
};

const updatePendingSettings = (userId, settings) => {
  pendingMemberSettings.value = {
    ...pendingMemberSettings.value,
    [userId]: {
      ...pendingMemberSettings.value[userId],
      ...settings,
    },
  };
};

const normalizedSearchQuery = computed(() =>
  searchQuery.value.trim().toLocaleLowerCase()
);

const visibleAgents = computed(() => {
  const list = [...agents.value].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''))
  );

  if (!normalizedSearchQuery.value) return list;

  return list.filter(agent => {
    const name = String(agent.name || '').toLocaleLowerCase();
    const email = String(agent.email || '').toLocaleLowerCase();
    return (
      name.includes(normalizedSearchQuery.value) ||
      email.includes(normalizedSearchQuery.value)
    );
  });
});

const visibleAgentIds = computed(() =>
  visibleAgents.value.map(agent => agent.id)
);

const allVisibleAgentsSelected = computed(
  () =>
    visibleAgentIds.value.length > 0 &&
    visibleAgentIds.value.every(agentId => selected.value.includes(agentId))
);

const someVisibleAgentsSelected = computed(
  () =>
    visibleAgentIds.value.some(agentId => selected.value.includes(agentId)) &&
    !allVisibleAgentsSelected.value
);

const isAgentSelected = agentId => selected.value.includes(agentId);

const toggleAgent = agentId => {
  if (isAgentSelected(agentId)) {
    updateSelected(selected.value.filter(id => id !== agentId));
    removePendingSettings([agentId]);
    return;
  }

  updateSelected([...selected.value, agentId]);
  ensurePendingSettings(agentId);
};

const toggleSelectAll = () => {
  if (allVisibleAgentsSelected.value) {
    updateSelected(
      selected.value.filter(agentId => !visibleAgentIds.value.includes(agentId))
    );
    removePendingSettings(visibleAgentIds.value);
    return;
  }

  updateSelected([...new Set([...selected.value, ...visibleAgentIds.value])]);
  visibleAgentIds.value.forEach(ensurePendingSettings);
};

const save = async () => {
  if (!props.team) return;
  saving.value = true;
  try {
    const pendingSettings = { ...pendingMemberSettings.value };
    await store.dispatch('teamMembers/update', {
      teamId: props.team.id,
      agentsList: selected.value,
    });

    await Promise.all(
      Object.entries(pendingSettings).flatMap(([rawUserId, settings]) => {
        const userId = Number(rawUserId);
        const defaults = defaultSettingsFor(userId);
        const updates = [];

        if (settings.rotationEnabled !== defaults.rotationEnabled) {
          updates.push(
            store.dispatch('teamMembers/setRotationEnabled', {
              teamId: props.team.id,
              userId,
              enabled: settings.rotationEnabled,
            })
          );
        }

        if (settings.role !== defaults.role) {
          updates.push(
            store.dispatch('teamMembers/setRole', {
              teamId: props.team.id,
              userId,
              role: settings.role,
            })
          );
        }

        return updates;
      })
    );

    await store.dispatch('teamMembers/get', { teamId: props.team.id });
    pendingMemberSettings.value = {};
    useAlert('Membros salvos');
    emit('saved');
  } catch (e) {
    useAlert(e?.message || 'Erro ao salvar membros');
  } finally {
    saving.value = false;
  }
};

const membersForRoles = computed(() =>
  selected.value
    .map(userId => loadedMemberById(userId) || agentById(userId))
    .filter(Boolean)
);

const normalizeRole = role =>
  String(role || '')
    .trim()
    .toLowerCase() === 'supervisor'
    ? 'supervisor'
    : 'user';

const roleOf = userId => {
  const pending = pendingMemberSettings.value[userId];
  if (pending) return pending.role;

  const m = loadedMemberById(userId);
  return normalizeRole(m?.aceleres_role);
};

const isRotationEnabled = userId => {
  const pending = pendingMemberSettings.value[userId];
  if (pending) return pending.rotationEnabled;

  const member = loadedMemberById(userId);
  return isTeamRotationEnabled(member);
};

const isSupervisor = userId => {
  const pending = pendingMemberSettings.value[userId];
  if (pending) return pending.role === 'supervisor';

  const member = loadedMemberById(userId);
  return isTeamSupervisor(member);
};

const setRole = async (userId, role) => {
  if (pendingMemberSettings.value[userId]) {
    updatePendingSettings(userId, { role });
    return;
  }

  if (roleOf(userId) === role || roleBusy.value[userId]) return;
  roleBusy.value = { ...roleBusy.value, [userId]: true };
  try {
    await store.dispatch('teamMembers/setRole', {
      teamId: props.team.id,
      userId,
      role,
    });
    useAlert(
      role === 'supervisor'
        ? 'Definido como Supervisor'
        : 'Supervisor removido; atendente mantido'
    );
  } catch (e) {
    useAlert(e?.message || 'Erro ao definir papel');
  } finally {
    roleBusy.value = { ...roleBusy.value, [userId]: false };
  }
};

const toggleSupervisor = userId =>
  setRole(userId, isSupervisor(userId) ? 'user' : 'supervisor');

const toggleRotation = async userId => {
  if (pendingMemberSettings.value[userId]) {
    updatePendingSettings(userId, {
      rotationEnabled: !isRotationEnabled(userId),
    });
    return;
  }

  if (roleBusy.value[userId]) return;

  const enabled = !isRotationEnabled(userId);
  roleBusy.value = { ...roleBusy.value, [userId]: true };
  try {
    await store.dispatch('teamMembers/setRotationEnabled', {
      teamId: props.team.id,
      userId,
      enabled,
    });
    useAlert(enabled ? 'Incluído no rodízio' : 'Removido do rodízio');
  } catch (e) {
    useAlert(e?.message || 'Erro ao atualizar o rodízio');
  } finally {
    roleBusy.value = { ...roleBusy.value, [userId]: false };
  }
};
</script>

<template>
  <Teleport to="body">
    <Transition name="team-create-modal">
      <div
        v-if="team"
        class="teams-page__members-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(15,23,42,0.55)] p-4 max-md:p-2"
        @click.self="close"
      >
        <div
          class="teams-page__members-modal flex max-h-[calc(100vh-2rem)] w-full max-w-5xl min-w-0 flex-col overflow-hidden rounded-[12px] bg-white shadow-[0_12px_40px_rgba(15,23,42,0.12)] max-md:h-[calc(100svh-1rem)] max-md:max-h-[calc(100svh-1rem)]"
          role="dialog"
          aria-modal="true"
          :aria-label="dialogLabel"
        >
          <header
            class="teams-page__members-modal-header flex flex-shrink-0 items-start justify-between gap-4 border-b border-n-weak px-6 py-5 max-md:p-4"
          >
            <div class="min-w-0">
              <h2
                class="teams-page__members-modal-title m-0 truncate text-xl font-semibold text-n-slate-12"
              >
                {{ modalTitle }}
              </h2>
              <p
                class="teams-page__members-modal-description mb-0 mt-2 text-sm leading-6 text-n-slate-11"
              >
                {{ modalText.description }}
              </p>
            </div>
            <button
              type="button"
              class="teams-page__members-modal-close inline-flex size-9 items-center justify-center rounded-full border border-n-weak bg-white text-n-slate-11 transition-colors hover:bg-n-slate-2 hover:text-n-slate-12"
              @click="close"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </header>

          <div
            class="teams-page__members-modal-body scrollbar-thin mx-2 min-h-0 min-w-0 flex w-auto flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-4 pb-6 pt-5 max-md:mx-0 max-md:p-4"
          >
            <form
              id="teams-page-members-modal-form"
              class="teams-page__members-modal-form flex min-w-0 flex-col gap-3"
              @submit.prevent="save"
            >
              <div
                class="teams-page__members-modal-toolbar flex w-full items-stretch gap-3 max-md:flex-col"
              >
                <label class="relative min-w-0 flex-[1_1_0]">
                  <span
                    class="i-lucide-search pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
                  />
                  <span class="sr-only">{{ modalText.searchPlaceholder }}</span>
                  <input
                    v-model="searchQuery"
                    type="search"
                    :placeholder="modalText.searchPlaceholder"
                    class="reset-base h-11 w-full rounded-[50px] border border-n-weak bg-transparent py-2 pl-12 pr-4 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-aurora-500"
                  />
                </label>
                <div
                  class="teams-page__members-modal-selected-count flex h-11 w-60 flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[50px] border border-n-weak bg-[#fbfcfd] px-4 text-sm font-medium text-n-slate-12 max-md:w-full"
                  aria-live="polite"
                >
                  <span
                    class="i-lucide-circle-check-big size-4 flex-shrink-0 text-emerald-600"
                  />
                  {{ selectedCountLabel }}
                </div>
              </div>

              <div
                role="button"
                tabindex="0"
                class="teams-page__members-select-all mt-1 flex w-full items-center gap-3 rounded-[8px] px-4 py-3 text-left transition-colors"
                @click="toggleSelectAll"
                @keydown.enter.prevent="toggleSelectAll"
                @keydown.space.prevent="toggleSelectAll"
              >
                <Checkbox
                  :model-value="allVisibleAgentsSelected"
                  :indeterminate="someVisibleAgentsSelected"
                  :title="modalText.selectAll"
                  @click.stop
                  @change="toggleSelectAll"
                />
                <span class="text-sm font-medium text-n-slate-12">
                  {{ modalText.selectAll }}
                </span>
              </div>

              <div class="teams-page__members-list flex min-w-0 flex-col gap-0">
                <div
                  v-for="agent in visibleAgents"
                  :key="agent.id"
                  role="button"
                  tabindex="0"
                  class="teams-page__members-card flex w-full min-w-0 items-center gap-3 border-b border-n-weak px-4 py-3 text-left transition-colors last:border-b-0"
                  :class="
                    isAgentSelected(agent.id)
                      ? 'bg-[#ecfdf3]'
                      : 'bg-white hover:bg-n-slate-2'
                  "
                  @click="toggleAgent(agent.id)"
                  @keydown.enter.prevent="toggleAgent(agent.id)"
                  @keydown.space.prevent="toggleAgent(agent.id)"
                >
                  <Checkbox
                    :model-value="isAgentSelected(agent.id)"
                    @click.stop
                    @change="() => toggleAgent(agent.id)"
                  />
                  <Avatar
                    :src="agent.thumbnail"
                    :name="agent.name"
                    :status="agent.availability_status"
                    :size="36"
                    hide-offline-status
                    rounded-full
                    class="flex-shrink-0"
                  />
                  <span class="min-w-0 flex-1 overflow-hidden">
                    <span
                      class="block truncate text-sm font-semibold text-n-slate-12"
                    >
                      {{ agent.name }}
                    </span>
                    <span class="block truncate text-sm text-n-slate-11">
                      {{ agent.email || '---' }}
                    </span>
                  </span>
                </div>

                <p
                  v-if="!visibleAgents.length"
                  class="mb-0 rounded-[8px] border border-dashed border-n-weak bg-[#fbfcfd] px-4 py-6 text-center text-sm text-n-slate-11"
                >
                  {{ modalText.noSearchResults }}
                </p>
              </div>
            </form>

            <!-- Aceleres — papéis independentes: rodízio + supervisor. -->
            <section
              v-if="membersForRoles.length"
              class="teams-page__members-modal-roles flex flex-col gap-3 rounded-[8px] border border-n-weak bg-[#fbfcfd] p-5 max-md:p-4"
            >
              <header>
                <h3 class="m-0 text-base font-semibold text-n-slate-12">
                  {{ modalText.rolesTitle }}
                </h3>
                <p class="mb-0 mt-1 text-sm text-n-slate-11">
                  {{ modalText.rolesDescription }}
                </p>
              </header>
              <ul class="m-0 flex list-none flex-col gap-2 p-0">
                <li
                  v-for="m in membersForRoles"
                  :key="m.id"
                  class="teams-page__members-modal-role-item flex items-center justify-between gap-3 rounded-[8px] border border-[#dbe1ea] bg-white px-4 py-3 max-md:flex-col max-md:items-stretch max-md:gap-2"
                >
                  <span
                    class="min-w-0 truncate text-sm font-medium text-n-slate-12"
                  >
                    {{ m.name }}
                  </span>
                  <div
                    class="flex flex-shrink-0 items-center gap-2 max-md:w-full"
                  >
                    <button
                      type="button"
                      class="h-8 rounded-full border px-3 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 max-md:h-9 max-md:flex-1"
                      :class="
                        isRotationEnabled(m.id)
                          ? 'border-[#BCEBD4] bg-[#ECFDF3] text-[#166534]'
                          : 'border-n-weak bg-[#F1F5F9] text-[#475569] hover:border-[#18A66A] hover:text-[#166534]'
                      "
                      :aria-pressed="isRotationEnabled(m.id)"
                      :disabled="roleBusy[m.id]"
                      @click="toggleRotation(m.id)"
                    >
                      {{
                        isRotationEnabled(m.id)
                          ? modalText.attendantRole
                          : modalText.nonAttendantHint
                      }}
                    </button>
                    <button
                      type="button"
                      class="h-8 rounded-full border px-3 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 max-md:h-9 max-md:flex-1"
                      :class="
                        isSupervisor(m.id)
                          ? 'border-[#FF5757] bg-[#FF5757] text-white shadow-sm'
                          : 'border-n-weak bg-white text-[#334155] hover:border-[#FF5757] hover:text-[#FF5757]'
                      "
                      :aria-pressed="isSupervisor(m.id)"
                      :disabled="roleBusy[m.id]"
                      @click="toggleSupervisor(m.id)"
                    >
                      {{ modalText.supervisorRole }}
                    </button>
                    <Spinner v-if="roleBusy[m.id]" :size="14" />
                  </div>
                </li>
              </ul>
            </section>

            <div class="flex justify-end pt-1 max-md:w-full">
              <NextButton
                type="submit"
                form="teams-page-members-modal-form"
                class="!rounded-[50px] max-md:!w-full"
                :label="modalText.saveMembers"
                :disabled="selected.length === 0"
                :is-loading="saving"
              />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.teams-page__members-modal-backdrop {
  isolation: isolate;
}

.teams-page__members-modal {
  position: relative;
  z-index: 10000;
}

.teams-page__members-modal-body {
  scrollbar-color: #cbd5e1 transparent;
  scrollbar-width: thin;
}

.teams-page__members-modal-body::-webkit-scrollbar {
  width: 6px;
}

.teams-page__members-modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.teams-page__members-modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}

.teams-page__members-modal-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.team-create-modal-enter-active,
.team-create-modal-leave-active {
  transition: opacity 0.2s ease;
}

.team-create-modal-enter-from,
.team-create-modal-leave-to {
  opacity: 0;
}
</style>
